<template>
    <TransitionRoot
        as="template"
        :show="state.open"
    >
        <Dialog
            as="div"
            class="relative z-6000"
            :open="state.open === true"
            @close="dialogClose(state.overlayPreventClose)"
        >
            <TransitionChild
                as="template"
                enter="ease-out duration-300"
                enter-from="opacity-0"
                enter-to="opacity-100"
                leave="ease-in duration-200"
                leave-from="opacity-100"
                leave-to="opacity-0"
            >
                <div class="fixed inset-0 bg-black opacity-75 transition-opacity" />
            </TransitionChild>

            <div class="fixed inset-0 z-10">
                <div
                    class="flex min-h-full items-center justify-center p-2 text-center sm:items-center sm:p-0"
                >
                    <TransitionChild
                        as="template"
                        enter="ease-out duration-300"
                        enter-from="opacity-0 translate-y-4 sm:translate-y-0 scale-0"
                        enter-to="opacity-100 translate-y-0 scale-100"
                        leave="ease-in duration-200"
                        leave-from="opacity-100 translate-y-0 scale-100"
                        leave-to="opacity-0 translate-y-4 sm:translate-y-0 scale-0"
                    >
                        <DialogPanel
                            :class="[
                                state.maxWidthMax === true > 0
                                    ? `w-full sm:max-w-7xl`
                                    : 'w-full sm:max-w-lg',
                                'relative transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:p-6',
                            ]"
                        >
                            <div class="absolute top-4 right-4 block">
                                <button
                                    type="button"
                                    class="cursor-pointer rounded-md bg-white text-gray-400 hover:text-gray-500"
                                    @click="dialogClose()"
                                >
                                    <span class="sr-only">Close</span>
                                    <MdiClose
                                        class="h-6 w-6"
                                        aria-hidden="true"
                                    />
                                </button>
                            </div>
                            <div class="min-h-fit max-w-[98%] gap-x-4 sm:flex sm:items-start">
                                <div
                                    class="mx-auto flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-gray-200 sm:mx-0 sm:h-10 sm:w-10"
                                >
                                    <MdiFileDocumentCheck
                                        class="h-6 w-6 text-gray-600"
                                        aria-hidden="true"
                                    />
                                </div>
                                <div
                                    class="sm:max-w-modal-full mt-3 w-full text-center sm:mt-0 sm:text-left"
                                >
                                    <DialogTitle
                                        as="h3"
                                        class="text-base leading-6 font-semibold text-gray-900 select-none"
                                    >
                                        {{ state.title }}
                                    </DialogTitle>

                                    <div
                                        v-if="
                                            Object.keys(templatePlaceholders.data).length === 0 &&
                                            saving.active === false &&
                                            saving.inter === false
                                        "
                                        class="pt-2 pb-4 shadow-sm"
                                    >
                                        <AlertBox
                                            state="info"
                                            :icon="MdiAlert"
                                            :title="t('templatePreviewNoPlaceholders')"
                                        />
                                    </div>
                                    <div
                                        v-if="
                                            Object.keys(templatePlaceholders.data).length > 0 &&
                                            saving.active === false &&
                                            saving.inter === false
                                        "
                                        class="pt-2 pb-4 shadow-sm"
                                    >
                                        <p class="text-sm">{{ state.text }}</p>
                                    </div>
                                    <!-- Loader -->
                                    <div
                                        v-if="state.open && saving.active === true"
                                        class="max-w-modal-full max-h-[80vh] min-h-64 w-full grow overflow-y-auto select-none"
                                    >
                                        <Spinner
                                            type="dots"
                                            :color="`text-blue-600`"
                                            :color2="`text-blue-400`"
                                            :text-color="`text-gray-200`"
                                            :overlay="true"
                                            :speed="1000"
                                            :dots="true"
                                            :dot-char="'.'"
                                            :dot-dual="false"
                                            :dot-reverse="false"
                                            :text="t('loading')"
                                            :inline="false"
                                            :pulse="true"
                                        />
                                    </div>
                                    <!-- All done -->
                                    <div
                                        v-if="saving.active === false && saving.inter === true"
                                        class="max-w-modal-full mt-3 max-h-[70vh] w-full grow overflow-y-auto select-none"
                                    >
                                        <component
                                            :is="previewPreset[templateModel.type].component"
                                            :config="previewConfig"
                                            :active="saving.inter === true"
                                        />
                                    </div>
                                    <!-- Editor -->
                                    <div
                                        v-show="
                                            state.open === true &&
                                            Object.keys(templatePlaceholders.data).length > 0 &&
                                            saving.inter === false
                                        "
                                        class="max-h-[72vh] w-full max-w-full grow overflow-y-auto"
                                    >
                                        <div
                                            v-for="(group, mindex) in sortArrayOfObjectsBy(
                                                usedGroups,
                                                'name',
                                            )"
                                            :key="mindex"
                                            class="my-6"
                                        >
                                            <AlertBox
                                                state="info"
                                                :icon="getTypeIcon(group.type)"
                                                :title="group.name"
                                                class-override="mb-4"
                                            >
                                                <p class="text-sm">
                                                    {{ group.description }}
                                                </p>
                                            </AlertBox>

                                            <div
                                                v-if="group.type === 'MULTIPLE'"
                                                class="w-full max-w-full overflow-x-auto"
                                            >
                                                <table
                                                    class="w-full table-auto border-collapse border border-slate-200"
                                                >
                                                    <thead>
                                                        <tr class="bg-gray-200">
                                                            <th
                                                                v-for="(
                                                                    th, index
                                                                ) in flatIteratorStack[
                                                                    group.name
                                                                ][0]"
                                                                :key="index"
                                                                class="min-w-56 px-2 py-1 text-sm"
                                                            >
                                                                <p class="text-gray-500">
                                                                    {{ th.name }}
                                                                </p>
                                                                <p
                                                                    v-if="th.name !== th.label"
                                                                    class="text-xs text-gray-600"
                                                                >
                                                                    {{ th.label }}
                                                                </p>
                                                            </th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        <tr
                                                            v-for="(
                                                                row, rowNumber
                                                            ) in flatIteratorStack[group.name][0]"
                                                            :key="rowNumber"
                                                            class="odd:bg-zinc-100 even:bg-gray-200"
                                                        >
                                                            <td
                                                                v-for="(
                                                                    td, index
                                                                ) in flatIteratorStack[group.name][
                                                                    rowNumber
                                                                ]"
                                                                :key="index"
                                                                class="px-2 py-1.5"
                                                            >
                                                                <input
                                                                    :id="`sample-key-${index}`"
                                                                    v-model="
                                                                        previewSampleData
                                                                            .valueCollection[
                                                                            td.group
                                                                        ][td.index][td.name]
                                                                    "
                                                                    :placeholder="td.name"
                                                                    type="text"
                                                                    name="key"
                                                                    autocomplete="off"
                                                                    class="block w-full border-0 border-b border-b-gray-300 bg-gray-50 px-2 py-1 text-xs shadow-sm focus:border-b-blue-500 focus:ring-0 focus:outline-hidden sm:text-sm"
                                                                />
                                                            </td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                            <div
                                                v-if="
                                                    group.type === 'SIMPLE' ||
                                                    group.type === 'IMAGECODE'
                                                "
                                                class="w-full max-w-full sm:grid sm:grid-cols-2 sm:justify-items-stretch sm:gap-x-4 sm:gap-y-2 md:grid-cols-3 lg:grid-cols-4"
                                            >
                                                <div
                                                    v-for="(item, index) in flatIteratorStack[
                                                        group.name
                                                    ][0]"
                                                    :key="index"
                                                    class="www flex flex-col justify-between gap-y-1 px-1 py-1"
                                                >
                                                    <label
                                                        :for="`sample-key-${index}`"
                                                        class="block text-sm font-medium text-gray-700 select-none"
                                                    >
                                                        <p
                                                            class="text-sm break-words text-gray-500"
                                                        >
                                                            {{ item.name }}
                                                        </p>
                                                        <p
                                                            v-if="item.name !== item.label"
                                                            class="text-xs text-gray-700"
                                                        >
                                                            {{ item.label }}
                                                        </p>
                                                    </label>
                                                    <div class="flex">
                                                        <input
                                                            :id="`sample-key-${index}`"
                                                            v-model="
                                                                previewSampleData.valueCollection[
                                                                    item.group
                                                                ][item.index][item.name]
                                                            "
                                                            :placeholder="item.name"
                                                            type="text"
                                                            name="key"
                                                            autocomplete="off"
                                                            class="block w-full rounded-md border border-gray-300 px-2 py-1 text-xs shadow-sm focus:border-blue-500 focus:ring-blue-500 focus:outline-hidden sm:text-sm"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div
                                v-show="saving.active === false && saving.inter === false"
                                class="mt-1 flex justify-end gap-2 select-none md:mt-4"
                            >
                                <input
                                    v-if="previewConfig.type === 'EMAIL'"
                                    id="email"
                                    v-model="recipient.email"
                                    type="text"
                                    name="email"
                                    autocomplete="off"
                                    placeholder="Email"
                                    :class="[
                                        validateEmail(recipient.email)
                                            ? 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                                            : 'border-red-300 focus:border-red-500 focus:ring-red-500',
                                        'mr-4 block w-full max-w-sm rounded-md border px-3 py-1 text-sm shadow-sm focus:outline-hidden',
                                    ]"
                                />
                                <ActionButton
                                    :value="t('buttonCancel')"
                                    state="neutral"
                                    :icon="MdiChevronLeft"
                                    @click="dialogClose()"
                                />
                                <ActionButton
                                    :value="t('buttonSend')"
                                    state="primary"
                                    :icon="MdiCubeSend"
                                    :is-disabled="sendButtonDisabled()"
                                    @click="createPreview()"
                                />
                            </div>
                        </DialogPanel>
                    </TransitionChild>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>
</template>

<script setup>
import { reactive, watch, inject } from 'vue';
import { getPlaceholder } from '../composables/usePlaceholders';
import { listPlaceholderRefs } from '../composables/usePlaceholderRefs';
import { extractRealDescription } from '../composables/usePlaceholderTypes';
import { getPreview } from '../composables/useTemplates';
import { t } from '../composables/i18n';
import {
    asleep,
    cloneData,
    searchObjectList,
    sortArrayOfObjectsBy,
    uuidCreate,
    validateEmail,
    //base64DecodeUTF,
} from '../composables/utils';
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue';
import {
    MdiFileDocumentCheck,
    MdiClose,
    MdiCodeBraces,
    MdiRepeat,
    MdiChevronLeft,
    MdiCubeSend,
    MdiAlert,
} from 'materialdesignicons-vue3/icons/';
import Spinner from '../ui/Spinner.vue';
import slugify from 'slugify';
import PDFPreview from '../preview-widgets/PDFPreview.vue';
import EmailPreview from '../preview-widgets/EmailPreview.vue';
import SMSPreview from '../preview-widgets/SMSPreview.vue';
import InboxPreview from '../preview-widgets/InboxPreview.vue';
import AlertBox from '../ui/AlertBox.vue';
import ActionButton from '../ui/ActionButton.vue';

defineExpose({
    name: 'TemplatePreviewModal',
});

const props = defineProps({
    templateVersionModel: {
        type: Object,
        default: () => {
            return {};
        },
    },
    templateModel: {
        type: Object,
        default: () => {
            return {};
        },
    },
    usedPlaceholders: {
        type: Object,
        default: () => {
            return {
                items: [],
                totalCount: 0,
            };
        },
    },
    state: {
        type: Object,
        default: () => {
            return {
                open: false,
                title: '',
                text: '',
                maxWidthMax: false,
                data: {},
                overlayPreventClose: true,
            };
        },
    },
});

const state = props.state;
const templateVersionModel = props.templateVersionModel;
const templateModel = props.templateModel;
let templatePlaceholders = reactive({
    data: {},
});
const usedPlaceholders = props.usedPlaceholders;
const previewSampleData = reactive({
    templateCode: templateModel.code,
    langCode: templateVersionModel.langCode,
    version: templateVersionModel.version,
    //duid: uuidCreate(),
    //pdfRendererType: 'PDFBOX',
    performAction: ['PREVIEW'],
    valueCollection: {},
    /* recipient: {
        email: '',
        partyId: '123456',
        phone: '',
    }, */
});
const recipient = reactive({
    email: '',
    partyId: '123456',
    phone: '',
});
const placeholders = inject('placeholders');

let flatIteratorStack = {};
let usedGroups = [];

const saving = reactive({
    active: false,
    inter: false,
    text: `${t('loading')}`,
});

const getTypeIcon = (type) => {
    if (type === 'MULTIPLE') {
        return MdiRepeat;
    }
    if (type === 'SIMPLE' || type === 'IMAGECODE') {
        return MdiCodeBraces;
    }
    return false;
};

const previewPreset = {
    PDF: {
        contentPrefix: 'data:application/pdf;base64,',
        fileName: 'preview',
        extension: 'pdf',
        events: ['download'],
        component: PDFPreview,
    },
    EMAIL: {
        contentPrefix: 'data:text/html;base64,',
        fileName: 'preview',
        extension: 'html',
        events: ['download', 'display', 'send'],
        component: EmailPreview,
    },
    SMS: {
        contentPrefix: 'data:text/plain;base64,',
        fileName: 'preview',
        extension: 'txt',
        events: ['download', 'display'],
        component: SMSPreview,
    },
    INBOX: {
        contentPrefix: 'data:text/xml;base64,',
        fileName: 'preview',
        extension: 'xml',
        events: ['download'],
        component: InboxPreview,
    },
};

const previewConfig = reactive({
    type: templateModel.type,
    preset: previewPreset[templateModel.type],
    duid: false,
    raw: '',
    contentType: '',
    state: {
        send: false,
        download: false,
        display: false,
    },
});

const globalPreviewResult = reactive({
    data: false,
});

const sendButtonDisabled = () => {
    return (
        saving.active === true ||
        saving.inter === true ||
        (!validateEmail(recipient.email) && previewConfig.type === 'EMAIL')
    );
};

const createPreview = async () => {
    saving.inter = false;
    if (previewConfig.duid !== false) {
        previewSampleData.duid = uuidCreate();
    }
    if (previewConfig.preset.events.includes('send')) {
        await sendTemplatePreview();
    }
    if (previewConfig.preset.events.includes('download')) {
        await downloadTemplatePreview();
    }
    if (previewConfig.preset.events.includes('display')) {
        await displayTemplatePreview();
    }
    globalPreviewResult.data = false;
    saving.inter = true;
};

const sendTemplatePreview = async () => {
    const sampleClone = cloneData(previewSampleData);
    sampleClone.performAction = ['SEND'];
    sampleClone.recipient = recipient;
    // USE UUID: sampleClone.duid = uuidCreate();
    sampleClone.attachmentIds = [];
    // ALTERRNATIVE setup: sampleClone.attachmentCollection = [];
    // ALTERRNATIVE setup: sampleClone.urlAttachmentCollection = [];
    /*
    ALTERRNATIVE setup:
    const sampleClone = {
        emailTemplate: cloneData(previewSampleData),
        recipient: recipient,
        attachmentCollection: [],
        urlAttachmentCollection: [],
        templateCode: previewSampleData.templateCode,
    }; */
    // ALTERRNATIVE setup sampleClone.emailTemplate.duid = uuidCreate();
    if (globalPreviewResult.data === false) {
        globalPreviewResult.data = await getPreview(sampleClone, saving);
    }
    // ALTERRNATIVE setup: const result = await sendPreview(sampleClone, saving);
    if (
        globalPreviewResult.data !== false &&
        globalPreviewResult.data.status &&
        globalPreviewResult.data.status === 'OK'
    ) {
        previewConfig.state.send = true;
        asleep(700);
    }
    return globalPreviewResult.data;
};

const downloadTemplatePreview = async () => {
    const sampleClone = cloneData(previewSampleData);
    sampleClone.performAction = ['PREVIEW'];
    if (sampleClone.recipient) {
        delete sampleClone.recipient;
    }
    if (globalPreviewResult.data === false) {
        globalPreviewResult.data = await getPreview(sampleClone, saving);
    }
    if (
        globalPreviewResult.data !== false &&
        globalPreviewResult.data.status &&
        globalPreviewResult.data.status === 'OK'
    ) {
        previewConfig.raw = `${globalPreviewResult.data.output.content}`;
        previewConfig.preset.fileName = `TEMPLATING-PREVIEW-${slugify(templateModel.code)}`;
        const downloadLink = document.createElement('a');
        downloadLink.href = `${previewConfig.preset.contentPrefix}${previewConfig.raw}`;
        downloadLink.download = `${previewConfig.preset.fileName}.${previewConfig.preset.extension}`;
        previewConfig.state.download = true;
        asleep(700);
        downloadLink.click();
    }
    return globalPreviewResult.data;
};

const displayTemplatePreview = async () => {
    const sampleClone = cloneData(previewSampleData);
    sampleClone.performAction = ['PREVIEW'];
    if (sampleClone.recipient) {
        delete sampleClone.recipient;
    }
    if (globalPreviewResult.data === false) {
        globalPreviewResult.data = await getPreview(sampleClone, saving);
    }
    if (
        globalPreviewResult.data !== false &&
        globalPreviewResult.data.status &&
        globalPreviewResult.data.status === 'OK'
    ) {
        previewConfig.preset.fileName = `TEMPLATING-PREVIEW-${slugify(templateModel.code)}`;
        previewConfig.raw = `${globalPreviewResult.data.output.content}`;
        previewConfig.state.display = true;
        asleep(700);
    }
    return globalPreviewResult.data;
};

// get TemplateVersion dataset and transform to flat structure
const getTemplatePlaceholderFromRef = async (templateVersionId) => {
    const placeholderRefs = (await listPlaceholderRefs({
        isValid: true,
        templateVersionId: templateVersionId,
    })) || {
        items: [],
        totalCount: 0,
    };
    //console.log('placeholderRefs', placeholderRefs);
    let placeholderGroups = {};
    for (let i in placeholderRefs.items) {
        const groupItem = await getPlaceholder(placeholderRefs.items[i].placeholderDefinitionId);
        //console.log('groupItem', groupItem);
        const flatItem = createFlatObject(groupItem, i);
        //console.log('flatItem', flatItem);
        placeholderGroups[flatItem.group] = flatItem.data;
        //console.log('placeholderGroups[flatItem.group]', placeholderGroups[flatItem.group]);
    }
    return placeholderGroups;
};

const createFlatObject = (groupItem, index) => {
    const result = {
        group: groupItem.name,
        sort: index,
        data: [],
    };
    if (!searchObjectList(usedGroups, 'name', groupItem.name)) {
        usedGroups.push({
            name: groupItem.name,
            type: groupItem.type,
            description: typeof groupItem.description === 'string' ? groupItem.description : '',
        });
    }
    for (let i in groupItem.sample) {
        result.data.push(groupItem.sample[i]);
    }
    return result;
};

const createPreviewDataset = () => {
    // Vytvoreni prazdnych skupin
    for (let i in usedPlaceholders) {
        const groupItem = usedPlaceholders[i];
        if (!(groupItem.group in previewSampleData.valueCollection)) {
            previewSampleData.valueCollection[groupItem.group] = [];
        }
    }
    //console.log('usedPlaceholders', usedPlaceholders);
    //console.log('templatePlaceholders', templatePlaceholders);

    // console.log('templatePlaceholders.data', templatePlaceholders.data);
    // Naplneni plnym datasetem
    for (let group in templatePlaceholders.data) {
        // add group
        flatIteratorStack[group] = [];
        if (group in previewSampleData.valueCollection) {
            const samples = templatePlaceholders.data[group];
            for (let sampleItemIndex in samples) {
                // add row
                flatIteratorStack[group][sampleItemIndex] = [];
                for (let sampleKey in samples[sampleItemIndex]) {
                    const search = usedPlaceholders.find(
                        (item) => item.group == group && item.ident == sampleKey,
                    );
                    if (typeof search === 'undefined') {
                        delete samples[sampleItemIndex][sampleKey];
                    } else {
                        const realLabel = extractRealDescription(
                            searchObjectList(placeholders, 'name', group),
                            sampleKey,
                        );
                        const flatData = {
                            group: group,
                            name: sampleKey,
                            data: samples[sampleItemIndex][sampleKey],
                            sort: `${group}.${sampleItemIndex}.${sampleKey}`,
                            index: sampleItemIndex,
                            label: realLabel,
                        };
                        // add row col aka item
                        flatIteratorStack[group][sampleItemIndex].push(flatData);
                    }
                }
                previewSampleData.valueCollection[group].push(samples[sampleItemIndex]);
            }
        }
    }
    //console.log('flatIteratorStack', flatIteratorStack);
    return previewSampleData;
};

watch(state, async (state) => {
    if (state.open === true) {
        // Vyber z databaze
        //previewSampleData.duid = uuidCreate();
        templatePlaceholders.data = {};
        usedGroups = [];
        templatePlaceholders.data = reactive(
            await getTemplatePlaceholderFromRef(templateVersionModel.templateVersionId),
        );
        flatIteratorStack = {};
        createPreviewDataset();
        globalPreviewResult.data = false;
        saving.inter = false;
    }
});

const dialogClose = (preventClose = false) => {
    if (preventClose === false) {
        state.open = false;
    }
};
</script>
